import { useEffect } from "react";
import type React from "react";
import type { FocalShot, MediaItem, Region } from "@gooner/shared";
import { adaptiveFramingOn, focalCrop, focalShotAt } from "@gooner/shared";

/**
 * Point a tile at what the eye lands on, instead of dead centre.
 *
 * `object-fit: cover` puts the largest tile-shaped rect at the middle of the frame and
 * calls it done. On the wide layouts that keeps 22% of the source and throws the subject
 * off the tile entirely in 44% of sampled frames. Adaptive Framing composes a transform
 * on top of that cover crop — scale 1 with no translate IS the old behaviour, so what
 * this adds is measured in the same units as what it replaces.
 *
 * Three things this deliberately does NOT do:
 *
 *   - No rAF loop. Shots average 1.8 per clip, so the transform changes about twice in
 *     ten seconds. It rides `timeupdate`, which the browser already fires ~4Hz and which
 *     is exact about currentTime, and writes the style DIRECTLY rather than through
 *     state — sixteen tiles re-rendering on a video clock is not free, and concurrent
 *     decode is already this wall's measured bottleneck (docs/perf/decode-load.md).
 *   - No transform while the clip is unknown. A clip the focal sweep has not reached has
 *     no shots, and the tile stays exactly as it was.
 *   - No motion under `prefers-reduced-motion`. A pan across a tile is a far stronger
 *     motion cue than the 600ms crossfade beside it, so the framing is applied but the
 *     glide between shots is not.
 */
export function useAdaptiveFraming(
  /** REFS, not elements. A ref read during render is still null — the callback ref runs
   *  at commit, after this hook's arguments were evaluated — so passing `.current` here
   *  would hand the effect a null on the very render that first has a clip to frame. */
  pairs: ReadonlyArray<{
    ref: React.MutableRefObject<HTMLVideoElement | null>;
    item: MediaItem | null;
    /** Where to read the clip's position. A sync follower on the captured path has no
     *  meaningful currentTime of its own — the MediaStream is live — so it borrows the
     *  leader's. Defaults to the element's own clock. */
    clock?: () => number;
    /** Compose with scaleX(-1). The framing is applied in content space and the whole
     *  framed result is then reflected, so a mirrored pair is a mirror of the SAME
     *  framing rather than a differently-framed tile. */
    mirrored?: boolean;
    /** Identity of the ELEMENT, not the clip. RegionPlayer keys each slot by a counter,
     *  so React unmounts and remounts the <video> on every showing — and a clip pinned to
     *  a region replays under the SAME id. Without this the effect never re-runs, and its
     *  listeners stay on an element that is no longer in the document: the tile frames
     *  once and never again. */
    epoch?: number | string;
  }>,
  region: Region,
  box: { w: number; h: number } | null,
  moveMs: number,
): void {
  const on = adaptiveFramingOn(region) && region.fit !== "contain";
  const tileAR = box && box.h > 0 ? box.w / box.h : 0;
  // Depend on ids and geometry, not object identity: the slots are rebuilt on every
  // render and the effect would otherwise reinstall its listeners on each one.
  const key = framingKey(pairs);

  useEffect(() => {
    if (!on || !tileAR) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

    const cleanups: Array<() => void> = [];
    for (const { ref, item, clock, mirrored } of pairs) {
      const el = ref.current;
      if (!el) continue;
      if (!item?.focal?.length || !item.width || !item.height) {
        // No framing for this clip. The hook still owns `transform`, so a mirrored tile
        // gets its reflection from here rather than from a JSX style that would fight it.
        if (mirrored) el.style.transform = "scaleX(-1)";
        continue;
      }
      const shots = item.focal;
      let applied = -1;
      let last = 0;

      const paint = () => {
        const now = clock ? clock() : el.currentTime;
        if (!Number.isFinite(now)) return;
        // A clip that went BACKWARDS looped — the follower's native loop, or the leader's
        // gap-bridge replay. Gliding from the last shot back to the first would sweep the
        // whole frame on every lap; a loop is a cut, so snap.
        const looped = now < last - 0.5;
        last = now;
        if (looped) applied = -1;
        const next = framingStyle(
          { shots, w: item.width!, h: item.height! },
          tileAR,
          now,
          { reduce, moveMs, applied, mirrored, snap: looped },
        );
        if (!next) return;
        applied = next.shot;
        el.style.transition = next.transition;
        el.style.transform = next.transform;
        el.style.objectPosition = next.objectPosition;
      };

      paint();
      el.addEventListener("timeupdate", paint);
      el.addEventListener("seeked", paint);
      el.addEventListener("loadeddata", paint);
      // The captured-stream path never advances this element's own clock, so nothing here
      // fires. Poll it instead — once a second is far finer than a shot boundary.
      const tick = clock ? window.setInterval(paint, 250) : 0;
      cleanups.push(() => {
        if (tick) window.clearInterval(tick);
        el.removeEventListener("timeupdate", paint);
        el.removeEventListener("seeked", paint);
        el.removeEventListener("loadeddata", paint);
        // Hand the element back to its UNFRAMED state — which for a mirrored follower is
        // the reflection, not nothing. Clearing outright would drop the mirror until the
        // next React render put it back, which a resize makes visible.
        el.style.transition = "";
        el.style.transform = mirrored ? "scaleX(-1)" : "";
        el.style.objectPosition = "";
      });
    }
    return () => {
      for (const c of cleanups) c();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, tileAR, key, moveMs]);
}

/**
 * The effect's identity: which ELEMENT is showing which clip.
 *
 * Keyed on the epoch as well as the clip because RegionPlayer keys each slot by a
 * counter, so React unmounts and remounts the <video> on every showing — and a clip
 * pinned to a region replays under the same id. On clip id alone the effect never
 * re-runs, its listeners stay on a detached element, and the tile frames exactly once.
 * That shipped: tiles framed on their first play and sat still on every loop after.
 */
export function framingKey(
  pairs: ReadonlyArray<{
    item: { id: string } | null;
    epoch?: number | string;
    mirrored?: boolean;
    clock?: unknown;
  }>,
): string {
  // `mirrored` and the presence of an external clock are part of the identity, not just
  // the element and the clip. The hook OWNS the transform — including the reflection —
  // so a mirror toggled on the wall changes nothing at all unless the effect re-runs.
  // It shipped that way for one revision: the button worked, the tile did not move.
  return pairs
    .map((p) => `${p.epoch ?? ""}:${p.item?.id ?? ""}:${p.mirrored ? "m" : ""}:${p.clock ? "c" : ""}`)
    .join("|");
}

/**
 * What a tile should look like right now, or null if nothing has changed.
 *
 * Pulled out of the effect so the decision is testable without a DOM or a renderer:
 * every interesting case here — a shot boundary, a rewind, reduced motion, the first
 * paint of a clip — is a branch that a screenshot would not pin down.
 */
export function framingStyle(
  clip: { shots: readonly FocalShot[]; w: number; h: number },
  tileAR: number,
  currentTime: number,
  opts: {
    reduce?: boolean;
    moveMs?: number;
    applied?: number;
    mirrored?: boolean;
    /** Cut rather than glide — a loop is not a camera move. */
    snap?: boolean;
  } = {},
): { shot: number; transform: string; transition: string; objectPosition: string } | null {
  const { reduce = false, moveMs = 700, applied = -1, mirrored = false, snap = false } = opts;
  if (!clip.shots?.length || !(tileAR > 0) || !(clip.w > 0) || !(clip.h > 0)) return null;
  const at = reduce ? clip.shots[0] : focalShotAt(clip.shots, currentTime);
  if (!at) return null;
  const shot = clip.shots.indexOf(at);
  if (shot === applied) return null;
  const c = focalCrop(at, clip.w, clip.h, tileAR);
  return {
    shot,
    // scale zooms; the bounded translate only spends the overhang that zoom creates.
    // The PAN lives in objectPosition — see focalCrop for why it cannot live here.
    // scaleX(-1) OUTERMOST so the framing happens in content space and the whole framed
    // result is reflected. The other order pans the mirrored image, which sends a synced
    // pair's cameras in opposite directions.
    transform: `${mirrored ? "scaleX(-1) " : ""}translate(${c.xPct}%, ${c.yPct}%) scale(${c.scale})`,
    objectPosition: `${c.posX}% ${c.posY}%`,
    // No transition on the FIRST paint of a clip: the tile is arriving through a
    // crossfade, and easing the framing in at the same time reads as a wobble rather than
    // a camera move. Later shots glide.
    transition:
      applied === -1 || reduce || snap
        ? ""
        : `transform ${moveMs}ms ease-in-out, object-position ${moveMs}ms ease-in-out`,
  };
}

/**
 * The most a clip's framing will zoom, for picking a rendition.
 *
 * The ladder sizes a rung off the tile's pixel box, so a tile that then zooms 1.5x is
 * showing a rung chosen for 1.0x blown up — visibly soft, and exactly the kind of
 * regression that looks like "the encoder got worse". One number for the whole clip
 * rather than per shot: a rung change mid-clip means a second fetch and a decode hitch,
 * which costs more than the sharpness it buys on the shots that zoom least.
 */
export function focalZoomCeiling(
  item: MediaItem | null,
  region: Region,
  tileAR: number,
): number {
  if (!item?.focal?.length || !adaptiveFramingOn(region) || region.fit === "contain") return 1;
  if (!(tileAR > 0) || !item.width || !item.height) return 1;
  // Asked of focalCrop rather than read off the shot, because the shot carries what the
  // CONTENT wants and the tile may not allow it: the area floor and the zoom cap both bite
  // here. Re-deriving those rules would be a second copy free to drift, and the cost of
  // being wrong is a rung too large — bytes spent on a magnification never applied.
  let max = 1;
  for (const s of item.focal) {
    max = Math.max(max, focalCrop(s, item.width, item.height, tileAR).scale);
  }
  return max;
}
